import { useState } from "react";
import { X, ShoppingBag, Plus, Minus, Trash2, Loader2, CreditCard, MessageCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useSettings, buildWhatsAppLink } from "@/context/SettingsContext";
import { api } from "@/lib/api";
import { trackBeginCheckout } from "@/lib/analytics";

// Flat shipping fee in the customer's chosen currency — mirrors backend `_compute_shipping_gbp`.
// Free Shipping toggles (global / UK / Ireland) apply ONLY when the cart subtotal meets the
// admin-editable minimum-spend threshold:
//   - Any wig-category item in cart  → £150 threshold (settings.free_shipping_min_wigs_gbp)
//   - Non-wig cart                    → £80  threshold (settings.free_shipping_min_other_gbp)
// Below threshold → the admin flat rate applies.
// Local Pickup always returns £0.
const WIG_CATEGORIES = new Set(["Luxury Wigs", "Braided Wig"]);

function freeShippingThresholdGbp(items, settings) {
  const hasWig = (items || []).some((i) => WIG_CATEGORIES.has(i.category || ""));
  const raw = hasWig ? settings?.free_shipping_min_wigs_gbp : settings?.free_shipping_min_other_gbp;
  // Explicit undefined/null check — 0 is a valid threshold ("no minimum").
  if (raw === undefined || raw === null || raw === "") return hasWig ? 150 : 80;
  const n = Number(raw);
  return Number.isFinite(n) ? n : (hasWig ? 150 : 80);
}

function computeShippingPreview({ currency, shippingMethod, settings, items, subtotalGbp }) {
  if (shippingMethod === "pickup") return { amount: 0, label: "FREE (Local Pickup)", free: true, reason: "pickup" };

  const uk = Number(settings?.uk_shipping_rate_gbp) || 10;
  const ie = Number(settings?.ireland_shipping_rate_eur) || 10;
  const global = Number(settings?.global_shipping_rate_gbp) || 25;
  const rateFromGbp = {
    USD: Number(settings?.usd_rate_override) || 1.27,
    CAD: Number(settings?.cad_rate_override) || 1.73,
    NGN: Number(settings?.ngn_rate_override) || 1800,
  }[currency] || 1;

  const paidAmount = () => {
    if (currency === "GBP") return uk;
    if (currency === "EUR") return ie;
    return Math.round(global * rateFromGbp * 100) / 100;
  };

  const globalFree = Boolean(settings?.free_shipping_global || settings?.free_shipping_enabled);
  const ukFree = currency === "GBP" && Boolean(settings?.free_shipping_uk);
  const ieFree = currency === "EUR" && Boolean(settings?.free_shipping_ireland);
  const toggleOn = globalFree || ukFree || ieFree;

  if (toggleOn) {
    const threshold = freeShippingThresholdGbp(items, settings);
    if ((subtotalGbp || 0) >= threshold) {
      const label = globalFree ? "FREE" : ukFree ? "FREE (UK)" : "FREE (Ireland)";
      return { amount: 0, label, free: true, reason: globalFree ? "global" : (ukFree ? "uk" : "ireland"), threshold };
    }
    // Below threshold — paid rate applies, but expose threshold so UI can nudge customer.
    return { amount: paidAmount(), free: false, threshold, belowThreshold: true };
  }

  return { amount: paidAmount(), free: false };
}

// Basic email validator — matches shape only; deeper validation left to Stripe/server.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (v) => EMAIL_RE.test(String(v || "").trim());

function CheckoutForm({ onStripe, busy, shippingMethod, setShippingMethod }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [touchedEmail, setTouchedEmail] = useState(false);

  const emailValid = isValidEmail(email);
  const nameValid = name.trim().length > 0;
  const canPay = nameValid && emailValid && !busy;

  const customer = () => ({
    customer_name: name.trim(),
    customer_phone: phone.trim(),
    customer_email: email.trim(),
    delivery_notes: notes.trim(),
  });

  const payWithCard = (e) => {
    e.preventDefault();
    if (!canPay) return;
    onStripe(customer());
  };

  return (
    <form onSubmit={payWithCard} className="space-y-3" data-testid="checkout-form">
      <div>
        <label className="block text-[0.65rem] uppercase tracking-[0.22em] text-white/55 mb-2">Your Name *</label>
        <input
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-4 py-2.5 text-white text-sm"
          data-testid="checkout-name"
          autoFocus
        />
      </div>
      <div>
        <label className="block text-[0.65rem] uppercase tracking-[0.22em] text-white/55 mb-2">Email *</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setTouchedEmail(true)}
          className={`w-full bg-[#0A0A0A] border outline-none px-4 py-2.5 text-white text-sm ${
            touchedEmail && !emailValid ? "border-red-500/70" : "border-[#2A2A2A] focus:border-[#D4AF37]"
          }`}
          data-testid="checkout-email"
          placeholder="you@example.com"
        />
        {touchedEmail && !emailValid && (
          <div className="text-[0.65rem] text-red-400 mt-1" data-testid="checkout-email-error">
            Please enter a valid email so we can send your order confirmation.
          </div>
        )}
      </div>
      <div>
        <label className="block text-[0.65rem] uppercase tracking-[0.22em] text-white/55 mb-2">Phone (optional)</label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-4 py-2.5 text-white text-sm"
          data-testid="checkout-phone"
        />
      </div>
      <div>
        <label className="block text-[0.65rem] uppercase tracking-[0.22em] text-white/55 mb-2">Delivery Address / Notes (optional)</label>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-4 py-2.5 text-white text-sm"
          data-testid="checkout-notes"
        />
      </div>

      {/* Delivery method selector — standard vs local pickup */}
      <div>
        <label className="block text-[0.65rem] uppercase tracking-[0.22em] text-white/55 mb-2">Delivery Method</label>
        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Delivery method">
          <button
            type="button"
            onClick={() => setShippingMethod("standard")}
            className={`px-3 py-2 text-xs uppercase tracking-[0.16em] border transition ${
              shippingMethod === "standard"
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                : "border-[#2A2A2A] text-white/60 hover:border-[#D4AF37]/50"
            }`}
            data-testid="shipping-method-standard"
          >
            Standard Delivery
          </button>
          <button
            type="button"
            onClick={() => setShippingMethod("pickup")}
            className={`px-3 py-2 text-xs uppercase tracking-[0.16em] border transition ${
              shippingMethod === "pickup"
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                : "border-[#2A2A2A] text-white/60 hover:border-[#D4AF37]/50"
            }`}
            data-testid="shipping-method-pickup"
          >
            Local Pickup · Free
          </button>
        </div>
      </div>

      <button
        type="submit"
        disabled={!canPay}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#F3E5AB] transition disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="checkout-pay-stripe"
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
        {busy ? "Redirecting…" : "Secure Checkout · Card, Google Pay, Apple Pay or Klarna"}
      </button>
      <p className="text-[0.65rem] text-white/45 text-center">
        Secure Stripe checkout. Shipping calculated at the next step based on your currency.
      </p>
    </form>
  );
}

// Modal shown before opening WhatsApp so we always capture Name + Email
// before handing off the enquiry to WhatsApp.
function WhatsAppContactModal({ open, onClose, onSubmit, busy }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);

  if (!open) return null;

  const emailValid = isValidEmail(email);
  const nameValid = name.trim().length > 0;
  const canSubmit = nameValid && emailValid && !busy;

  const handle = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    onSubmit({ name: name.trim(), email: email.trim() });
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center px-4" data-testid="whatsapp-contact-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <form
        onSubmit={handle}
        className="relative w-full max-w-md bg-[#0A0A0A] border border-[#D4AF37]/40 p-6 space-y-4"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[0.65rem] uppercase tracking-[0.28em] text-[#D4AF37] mb-1">Before we open WhatsApp</div>
            <div className="font-serif text-xl text-white">Your Contact Details</div>
            <p className="text-white/55 text-xs mt-1">
              So we can follow up with you if the WhatsApp thread gets buried.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-white/60 hover:text-[#D4AF37] -mt-1 -mr-1"
            data-testid="whatsapp-contact-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <label className="block text-[0.65rem] uppercase tracking-[0.22em] text-white/55 mb-2">Your Name *</label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-4 py-2.5 text-white text-sm"
            data-testid="whatsapp-contact-name"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-[0.65rem] uppercase tracking-[0.22em] text-white/55 mb-2">Email *</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched(true)}
            className={`w-full bg-[#0F0F0F] border outline-none px-4 py-2.5 text-white text-sm ${
              touched && !emailValid ? "border-red-500/70" : "border-[#2A2A2A] focus:border-[#D4AF37]"
            }`}
            data-testid="whatsapp-contact-email"
            placeholder="you@example.com"
          />
          {touched && !emailValid && (
            <div className="text-[0.65rem] text-red-400 mt-1">Please enter a valid email address.</div>
          )}
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] text-black text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#20BF5C] transition disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="whatsapp-contact-submit"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageCircle className="w-5 h-5" />}
          Continue to WhatsApp
        </button>
      </form>
    </div>
  );
}

export default function CartDrawer() {
  const { items, isOpen, closeCart, updateQuantity, removeItem, totalGbp, totalQuantity, clear } = useCart();
  const { currency, convertAmount } = useCurrency();
  const { settings } = useSettings();
  const [stage, setStage] = useState("cart"); // cart | checkout
  const [busy, setBusy] = useState(false);
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [waModalOpen, setWaModalOpen] = useState(false);
  const [waBusy, setWaBusy] = useState(false);

  const shipping = computeShippingPreview({
    currency,
    shippingMethod,
    settings,
    items,
    subtotalGbp: totalGbp,
  });
  const freeShipping = shipping.free && shipping.reason !== "pickup";
  // Precise conversion for cart totals — mirrors backend Stripe calc exactly so the
  // customer sees the same number in the cart as on the Stripe page. Uses 2dp for all
  // currencies (except NGN which shows whole amounts). Not routed through the
  // merchant-favouring `niceRound` used for product-tile prices.
  const currencyRateGbp = {
    GBP: 1,
    EUR: Number(settings?.eur_rate_override) || 1.17,
    USD: Number(settings?.usd_rate_override) || 1.27,
    CAD: Number(settings?.cad_rate_override) || 1.73,
    NGN: Number(settings?.ngn_rate_override) || 1800,
  }[currency] || 1;
  const fmt = (gbp) => {
    const converted = gbp * currencyRateGbp;
    const symbols = { GBP: "£", EUR: "€", USD: "$", CAD: "C$", NGN: "₦" };
    const sym = symbols[currency] || "£";
    if (currency === "NGN") return `${sym}${Math.round(converted).toLocaleString("en-GB")}`;
    return `${sym}${converted.toFixed(2)}`;
  };
  const shippingGbp = shipping.free ? 0 : shipping.amount / currencyRateGbp;
  const subtotalDisplay = fmt(totalGbp);
  const totalDisplay = fmt(totalGbp + shippingGbp);
  const shippingDisplay = shipping.free ? "FREE" : fmt(shippingGbp);

  // WhatsApp checkout — secondary option below the Stripe button.
  // Requires Name + Email (collected via WhatsAppContactModal) before opening wa.me.
  // Also POSTs to /api/orders so the sale appears in the Admin Orders Dashboard.
  const openWhatsAppCheckout = async ({ name, email }) => {
    setWaBusy(true);
    let orderId = "";
    try {
      const { data } = await api.post("/orders", {
        customer_name: name || "WhatsApp Enquiry",
        customer_email: email || "",
        items: items.map((i) => ({
          collection_id: i.id,
          name: i.name,
          category: i.category,
          price_gbp: i.price_gbp_string,
          unit_amount_gbp: i.unit_amount_gbp,
          quantity: i.quantity,
          image: "",  // never embed data URLs in orders
        })),
        currency,
        converted_total: totalDisplay,
      });
      orderId = data?.id || "";
    } catch {
      // Non-fatal — we still hand off to WhatsApp.
    }

    const lines = [];
    lines.push("*New Order enquiry from Luxury Hair by DEMJ*");
    lines.push("");
    if (name) lines.push(`*Name:* ${name}`);
    if (email) lines.push(`*Email:* ${email}`);
    if (orderId) lines.push(`*Order ID:* ${orderId.slice(0, 8).toUpperCase()}`);
    lines.push("");
    lines.push("*Items:*");
    items.forEach((i, idx) => {
      const unit = fmt(i.unit_amount_gbp || 0);
      const lineTotal = fmt((i.unit_amount_gbp || 0) * (i.quantity || 1));
      lines.push(`${idx + 1}. ${i.name}  —  ${i.quantity} × ${unit}  =  ${lineTotal}`);
    });
    lines.push("");
    lines.push(`*Subtotal (${currency}):* ${subtotalDisplay}`);
    lines.push(`*Shipping (${currency}):* ${shippingDisplay}`);
    lines.push(`*Total (${currency}):* ${totalDisplay}`);
    lines.push("");
    lines.push("Please confirm availability and share payment details. Thank you!");
    const url = buildWhatsAppLink(settings, lines.join("\n"));
    setWaBusy(false);
    setWaModalOpen(false);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Stripe Checkout — server computes total (subtotal + flat shipping by currency/method)
  const handleStripeCheckout = async (customer) => {
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/create", {
        items: items.map((i) => ({ collection_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        customer_name: customer.customer_name || "",
        customer_email: customer.customer_email || "",
        customer_phone: customer.customer_phone || "",
        delivery_notes: customer.delivery_notes || "",
        currency,
        shipping_method: shippingMethod,
      });
      if (data?.url) {
        // Persist a signal so we can clear the cart when we return to /?checkout_status=success
        window.sessionStorage.setItem("pending_stripe_session", data.session_id);
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error("Stripe checkout failed:", err);
      window.alert("Could not start Stripe checkout. Please try again in a moment.");
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeCart} />
      <aside
        className="absolute right-0 top-0 h-full w-full sm:w-[460px] bg-[#0A0A0A] border-l border-[#1F1F1F] flex flex-col overflow-hidden"
        role="dialog"
        aria-label="Shopping cart"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-[#1F1F1F]">
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-5 h-5 text-[#D4AF37]" />
            <div className="font-serif text-lg text-white">
              {stage === "cart" ? "Your Bag" : "Checkout"}
            </div>
            {totalQuantity > 0 && (
              <span className="text-[0.65rem] uppercase tracking-[0.22em] text-white/55">
                {totalQuantity} item{totalQuantity > 1 ? "s" : ""}
              </span>
            )}
          </div>
          <button
            onClick={closeCart}
            aria-label="Close cart"
            className="text-white/70 hover:text-[#D4AF37] p-2 -mr-2"
            data-testid="cart-close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16" data-testid="cart-empty">
              <div className="w-16 h-16 rounded-full bg-[#0F0F0F] border border-[#D4AF37]/40 flex items-center justify-center mb-5">
                <ShoppingBag className="w-7 h-7 text-[#D4AF37]/60" />
              </div>
              <div className="font-serif text-xl text-white">Your bag is empty</div>
              <p className="text-white/55 text-sm mt-2 max-w-xs">
                Browse the collections and add the pieces you love. Secure checkout with card, Google Pay, Apple Pay or Klarna.
              </p>
            </div>
          ) : stage === "cart" ? (
            <ul className="space-y-4" data-testid="cart-items">
              {items.map((i) => (
                <li key={i.id} className="flex gap-3 border border-[#1F1F1F] bg-[#0F0F0F] p-3" data-testid={`cart-item-${i.id}`}>
                  <div className="w-20 h-20 shrink-0 bg-[#0A0A0A] border border-[#1F1F1F] overflow-hidden">
                    {i.image ? (
                      <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#D4AF37]/60 text-[0.6rem] uppercase tracking-[0.18em] p-2 text-center">
                        {i.category || "Item"}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-serif text-white text-sm leading-tight">{i.name}</div>
                      <button
                        onClick={() => removeItem(i.id)}
                        aria-label="Remove"
                        className="text-white/40 hover:text-red-400 -mt-0.5 -mr-0.5"
                        data-testid={`cart-remove-${i.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="text-[0.65rem] uppercase tracking-[0.18em] text-white/45">{i.category}</div>
                    <div className="mt-auto flex items-center justify-between pt-2">
                      <div className="inline-flex items-center border border-[#2A2A2A]">
                        <button
                          onClick={() => updateQuantity(i.id, (i.quantity || 1) - 1)}
                          className="w-7 h-7 text-white/70 hover:text-[#D4AF37] flex items-center justify-center"
                          aria-label="Decrease"
                          data-testid={`cart-dec-${i.id}`}
                          disabled={(i.quantity || 1) <= 1}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-7 text-center text-sm text-white" data-testid={`cart-qty-${i.id}`}>{i.quantity}</span>
                        <button
                          onClick={() => updateQuantity(i.id, (i.quantity || 1) + 1)}
                          className="w-7 h-7 text-white/70 hover:text-[#D4AF37] flex items-center justify-center"
                          aria-label="Increase"
                          data-testid={`cart-inc-${i.id}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="text-[#D4AF37] text-sm font-semibold" data-testid={`cart-line-${i.id}`}>
                        {fmt((i.unit_amount_gbp || 0) * (i.quantity || 1))}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <CheckoutForm
              onStripe={handleStripeCheckout}
              busy={busy}
              shippingMethod={shippingMethod}
              setShippingMethod={setShippingMethod}
            />
          )}
        </div>

        {items.length > 0 && stage === "cart" && (
          <footer className="border-t border-[#1F1F1F] px-5 py-4 space-y-3">
            {freeShipping && (
              <div
                className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-[0.7rem] uppercase tracking-[0.18em] font-semibold px-3 py-2"
                data-testid="cart-free-shipping-note"
              >
                <span className="text-emerald-400">✓</span>
                Your order qualifies for FREE SHIPPING
              </div>
            )}
            {shipping.belowThreshold && shipping.threshold && (
              <div
                className="flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#D4AF37] text-[0.7rem] uppercase tracking-[0.18em] font-semibold px-3 py-2"
                data-testid="cart-below-free-shipping-threshold"
              >
                <span aria-hidden>◆</span>
                <span>
                  Spend {fmt(Math.max(0, shipping.threshold - totalGbp))} more to unlock FREE shipping
                </span>
              </div>
            )}
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-white/70">
                <span className="text-[0.65rem] uppercase tracking-[0.22em] text-white/50">Subtotal</span>
                <span data-testid="cart-subtotal">{subtotalDisplay}</span>
              </div>
              <div className="flex justify-between text-white/70">
                <span className="text-[0.65rem] uppercase tracking-[0.22em] text-white/50">Shipping</span>
                <span className={shipping.free ? "text-emerald-400 font-semibold" : ""} data-testid="cart-shipping">
                  {shippingDisplay}
                </span>
              </div>
              <div className="flex justify-between items-baseline pt-2 border-t border-[#1F1F1F]">
                <div>
                  <div className="text-[0.65rem] uppercase tracking-[0.22em] text-white/55">Total ({currency})</div>
                </div>
                <div className="font-serif text-2xl text-[#D4AF37]" data-testid="cart-total">{totalDisplay}</div>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => clear()}
                className="text-[0.65rem] text-white/45 hover:text-red-400 uppercase tracking-[0.18em]"
                data-testid="cart-clear"
              >
                Clear bag
              </button>
            </div>
            <button
              onClick={() => {
                trackBeginCheckout({ items, subtotalGbp: totalGbp });
                setStage("checkout");
              }}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#F3E5AB] transition ${settings?.secure_checkout_enabled === false ? "hidden" : ""}`}
              data-testid="cart-checkout"
            >
              <CreditCard className="w-5 h-5" />
              Secure Checkout
            </button>
            <button
              onClick={() => setWaModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#25D366] text-black text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#20BF5C] transition"
              data-testid="cart-checkout-whatsapp"
            >
              <MessageCircle className="w-5 h-5" />
              Checkout via WhatsApp
            </button>
            <p className="text-[0.65rem] text-white/40 text-center">
              {settings?.secure_checkout_enabled === false
                ? "Enquire via WhatsApp — a member of our team will confirm your order and share payment details."
                : "Pay with card, Google Pay, Apple Pay or Klarna — or enquire via WhatsApp."}
            </p>
          </footer>
        )}

        {items.length > 0 && stage === "checkout" && (
          <footer className="border-t border-[#1F1F1F] px-5 py-3 space-y-1">
            <div className="flex justify-between text-white/60 text-[0.7rem] uppercase tracking-[0.18em]">
              <span>Subtotal</span>
              <span>{subtotalDisplay}</span>
            </div>
            <div className="flex justify-between text-white/60 text-[0.7rem] uppercase tracking-[0.18em]">
              <span>Shipping</span>
              <span className={shipping.free ? "text-emerald-400 font-semibold" : ""}>{shippingDisplay}</span>
            </div>
            <div className="flex justify-between items-center pt-1 border-t border-[#1F1F1F] mt-1">
              <button
                onClick={() => setStage("cart")}
                className="text-xs text-white/55 hover:text-[#D4AF37] uppercase tracking-[0.18em]"
                data-testid="checkout-back"
              >
                ← Back to bag
              </button>
              <div className="text-right">
                <div className="text-[0.6rem] uppercase tracking-[0.18em] text-white/45">Total ({currency})</div>
                <div className="font-serif text-lg text-[#D4AF37]" data-testid="checkout-total">{totalDisplay}</div>
              </div>
            </div>
          </footer>
        )}
      </aside>
      <WhatsAppContactModal
        open={waModalOpen}
        onClose={() => setWaModalOpen(false)}
        onSubmit={openWhatsAppCheckout}
        busy={waBusy}
      />
    </div>
  );
}
